/**
 * BillingSettings — tax + late-fee + providers (read-only picker for now).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, AlertTriangle, Plug } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

export default function BillingSettings() {
  const { t } = useI18n();
  const [tax, setTax] = useState(null);
  const [late, setLate] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t1, t2, t3] = await Promise.all([
        api.get("/admin/billing/tax-config"),
        api.get("/admin/billing/late-fee-policy"),
        api.get("/admin/billing/providers"),
      ]);
      setTax(t1.data);
      setLate(t2.data);
      setProviders(t3.data || []);
    } catch (e) {
      // ignore
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const saveTax = async () => {
    try {
      await api.patch("/admin/billing/tax-config", {
        currency: tax.currency,
        tax_label: tax.tax_label,
        tax_rate_percent: Number(tax.tax_rate_percent),
        tax_id_label: tax.tax_id_label,
        tax_id_value: tax.tax_id_value,
      });
      toast.success(t("billing.messages.settings_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };
  const saveLate = async () => {
    try {
      await api.patch("/admin/billing/late-fee-policy", {
        enabled: !!late.enabled,
        grace_period_days: Number(late.grace_period_days),
        fee_kind: late.fee_kind,
        fee_amount: Number(late.fee_amount),
        max_late_fees_per_invoice: Number(late.max_late_fees_per_invoice),
      });
      toast.success(t("billing.messages.settings_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };

  if (loading || !tax || !late) {
    return (
      <main className="p-6 lg:p-10">
        <p className="font-serif italic text-[#525252]">{t("billing.common.loading")}</p>
      </main>
    );
  }

  return (
    <main className="p-6 lg:p-10 space-y-8" data-testid="admin-billing-settings">
      <AdminHeader eyebrow={t("billing.settings.eyebrow")} title={t("billing.settings.title")} />

      <section
        className="rounded-3xl bg-white border border-[#EEF0F4] p-6 lg:p-8"
        data-testid="billing-settings-tax-section"
      >
        <div className="flex items-center gap-2 mb-4">
          <Wallet size={16} strokeWidth={1.6} className="text-[#1F3A2A]" />
          <h3 className="font-display text-lg text-[#1A1B2E]">{t("billing.settings.tax_section")}</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field
            label={t("billing.settings.tax_currency")}
            value={tax.currency || "AED"}
            onChange={(v) => setTax({ ...tax, currency: v.toUpperCase() })}
            testid="billing-settings-tax-currency"
            maxLength={4}
          />
          <Field
            label={t("billing.settings.tax_label")}
            value={tax.tax_label || ""}
            onChange={(v) => setTax({ ...tax, tax_label: v })}
            testid="billing-settings-tax-label"
          />
          <Field
            label={t("billing.settings.tax_rate")}
            type="number"
            value={tax.tax_rate_percent ?? 0}
            onChange={(v) => setTax({ ...tax, tax_rate_percent: v })}
            testid="billing-settings-tax-rate"
            step="0.1"
          />
          <Field
            label={t("billing.settings.tax_id_label")}
            value={tax.tax_id_label || ""}
            onChange={(v) => setTax({ ...tax, tax_id_label: v })}
            testid="billing-settings-tax-id-label"
          />
          <Field
            label={t("billing.settings.tax_id_value")}
            value={tax.tax_id_value || ""}
            onChange={(v) => setTax({ ...tax, tax_id_value: v })}
            testid="billing-settings-tax-id-value"
          />
        </div>
        <div className="mt-4">
          <button
            onClick={saveTax}
            data-testid="billing-settings-tax-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            {t("billing.common.save")}
          </button>
        </div>
      </section>

      <section
        className="rounded-3xl bg-white border border-[#EEF0F4] p-6 lg:p-8"
        data-testid="billing-settings-late-fee-section"
      >
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={16} strokeWidth={1.6} className="text-[#A11D1D]" />
          <h3 className="font-display text-lg text-[#1A1B2E]">{t("billing.settings.late_fee_section")}</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1B2E]">
            <input
              type="checkbox"
              checked={!!late.enabled}
              onChange={(e) => setLate({ ...late, enabled: e.target.checked })}
              data-testid="billing-settings-late-enabled"
            />
            {t("billing.settings.late_fee_enabled")}
          </label>
          <Field
            label={t("billing.settings.late_fee_grace")}
            type="number"
            value={late.grace_period_days}
            onChange={(v) => setLate({ ...late, grace_period_days: v })}
            testid="billing-settings-late-grace"
          />
          <label className="block">
            <span className="eyebrow">{t("billing.settings.late_fee_kind")}</span>
            <select
              value={late.fee_kind}
              onChange={(e) => setLate({ ...late, fee_kind: e.target.value })}
              data-testid="billing-settings-late-kind"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
            >
              <option value="fixed">Fixed</option>
              <option value="percent">Percent</option>
            </select>
          </label>
          <Field
            label={t("billing.settings.late_fee_amount")}
            type="number"
            value={late.fee_amount}
            onChange={(v) => setLate({ ...late, fee_amount: v })}
            step="0.01"
            testid="billing-settings-late-amount"
          />
          <Field
            label={t("billing.settings.late_fee_max")}
            type="number"
            value={late.max_late_fees_per_invoice}
            onChange={(v) => setLate({ ...late, max_late_fees_per_invoice: v })}
            testid="billing-settings-late-max"
          />
        </div>
        <div className="mt-4">
          <button
            onClick={saveLate}
            data-testid="billing-settings-late-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            {t("billing.common.save")}
          </button>
        </div>
      </section>

      <section
        className="rounded-3xl bg-white border border-[#EEF0F4] p-6 lg:p-8"
        data-testid="billing-settings-providers-section"
      >
        <div className="flex items-center gap-2 mb-2">
          <Plug size={16} strokeWidth={1.6} className="text-[#3D5C46]" />
          <h3 className="font-display text-lg text-[#1A1B2E]">{t("billing.settings.providers_section")}</h3>
        </div>
        <p className="text-sm text-[#6B7280] max-w-2xl">{t("billing.settings.providers_blurb")}</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
          {providers.map((p) => (
            <li
              key={p.key}
              data-testid={`billing-provider-${p.key}`}
              className="flex items-center justify-between rounded-2xl border border-[#EEF0F4] px-4 py-3 bg-[#FAF9F1]/40"
            >
              <div>
                <div className="text-sm font-medium text-[#1A1B2E]">{p.display_name}</div>
                <div className="eyebrow mt-0.5">
                  {t(`billing.settings.provider_category_${p.category}`)}
                </div>
              </div>
              <span
                className={`text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${
                  p.enabled
                    ? "bg-[#E6EFE8] text-[#1F3A2A]"
                    : "bg-[#EEF0F4] text-[#9CA3AF]"
                }`}
              >
                {p.enabled
                  ? t("billing.settings.provider_status_enabled")
                  : t("billing.settings.provider_status_disabled")}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", step, testid, maxLength }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type}
        value={value}
        step={step}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
      />
    </label>
  );
}
